/**
 * 统一播放器 - 协议识别与地址归一化工具
 *
 * 分发规则：
 * 1. 地址包含 qvs-live（七牛 QVS 播放域名）→ 七牛 QNRTPlayer（WebRTC 低延迟）
 *    - rtmp:// 地址按 QVS 地址互推规则归一化为 webrtc://
 *    - 端口按页面协议自适应：http 页面用 1240，https 页面用 447
 * 2. 其他地址按协议分发：
 *    - .m3u8 → hls.js
 *    - .flv  → mpegts.js
 *    - rtmp:// → 浏览器不支持，返回错误
 *    - 其他 → 无法识别，返回错误
 */

// 播放引擎类型
export const ENGINE = {
  QNRTC: 'qnrtc', // 七牛 QNRTPlayer（WebRTC）
  HLS: 'hls',     // hls.js
  FLV: 'flv',     // mpegts.js
  NATIVE: 'native', // 原生 video（Safari 原生 HLS）
  UNKNOWN: 'unknown'
}

/**
 * 判断是否为七牛 QVS 播放地址
 * 规则：地址中包含 qvs-live 域名标识
 */
export function isQiniuQvsUrl(url) {
  return typeof url === 'string' && url.indexOf('qvs-live') !== -1
}

/**
 * 七牛 QVS 地址归一化为 webrtc:// 播放地址
 *
 * QVS 地址互推规则（同一流的四种地址可互推）：
 *   rtmp:  rtmp://域名:2045/空间id/流id?token=xxx
 *   webrtc: webrtc://域名:1240或447/空间id/流id?token=xxx
 *   hls:   http(s)://域名:1240或447/空间id/流id.m3u8?token=xxx
 *   flv:   http(s)://域名:1240或447/空间id/流id.flv?token=xxx
 *
 * 端口规则：http 页面用 1240，https 页面用 447（需 QVS 域名配置 SSL 证书）
 */
export function normalizeToWebrtcUrl(url) {
  if (typeof url !== 'string' || url === '') return url
  if (url.indexOf('webrtc://') === 0) {
    return adaptWebrtcPort(url)
  }
  // rtmp:// 推流/播放地址 → webrtc 播放地址
  if (url.indexOf('rtmp://') === 0) {
    const rest = url.slice('rtmp://'.length)
    return adaptWebrtcPort('webrtc://' + rest)
  }
  // http(s) hls/flv 地址 → webrtc 播放地址
  const httpMatch = url.match(/^https?:\/\/([^/]+)(\/[^?]*)/)
  if (httpMatch) {
    const host = httpMatch[1].replace(/:\d+$/, '') // 去掉端口
    const path = httpMatch[2].replace(/\.(m3u8|flv)$/i, '') // 去掉扩展名
    const query = url.indexOf('?') !== -1 ? url.slice(url.indexOf('?')) : ''
    return adaptWebrtcPort('webrtc://' + host + path + query)
  }
  return url
}

/**
 * 按页面协议适配 webrtc 地址端口
 * http 页面 → 1240；https 页面 → 447
 */
function adaptWebrtcPort(url) {
  const isHttpsPage = window.location.protocol === 'https:'
  const targetPort = isHttpsPage ? '447' : '1240'
  // webrtc://host:port/path → webrtc://host:targetPort/path
  const m = url.match(/^(webrtc:\/\/[^/:]+):(\d+)(\/.*)?$/)
  if (m) {
    return m[1] + ':' + targetPort + (m[3] || '')
  }
  // 无端口则补充默认端口
  const m2 = url.match(/^(webrtc:\/\/[^/]+)(\/.*)?$/)
  if (m2) {
    return m2[1] + ':' + targetPort + (m2[2] || '')
  }
  return url
}

/**
 * 识别播放地址应使用的引擎
 * 返回 { engine, playUrl, error }
 */
export function resolveEngine(url) {
  if (typeof url !== 'string' || url.trim() === '') {
    return { engine: ENGINE.UNKNOWN, playUrl: '', error: '播放地址为空' }
  }
  const trimmed = url.trim()

  // 规则1：七牛 QVS 地址（包含 qvs-live）统一走 QNRTPlayer
  if (isQiniuQvsUrl(trimmed)) {
    return { engine: ENGINE.QNRTC, playUrl: normalizeToWebrtcUrl(trimmed), error: '' }
  }

  // 规则2：其他平台按协议分发
  if (trimmed.indexOf('rtmp://') === 0) {
    return {
      engine: ENGINE.UNKNOWN,
      playUrl: '',
      error: '浏览器不支持 RTMP 播放，请使用 HLS(.m3u8) 或 FLV 播放地址'
    }
  }
  if (trimmed.indexOf('webrtc://') === 0) {
    return {
      engine: ENGINE.UNKNOWN,
      playUrl: '',
      error: '该 WebRTC 地址非七牛云地址，暂不支持播放'
    }
  }
  if (/\.m3u8(\?|$)/i.test(trimmed)) {
    // Safari 原生支持 HLS
    if (typeof navigator !== 'undefined' &&
        /^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
      return { engine: ENGINE.NATIVE, playUrl: trimmed, error: '' }
    }
    return { engine: ENGINE.HLS, playUrl: trimmed, error: '' }
  }
  if (/\.flv(\?|$)/i.test(trimmed)) {
    return { engine: ENGINE.FLV, playUrl: trimmed, error: '' }
  }

  return {
    engine: ENGINE.UNKNOWN,
    playUrl: '',
    error: '无法识别的播放地址格式，支持：七牛云地址、HLS(.m3u8)、FLV(.flv)'
  }
}
